import axios from 'axios';

/**
 * Candidate Evaluations — typed API client.
 *
 * Centralises calls against /api/candidate-evaluations/*. The "evaluation" is a
 * GLOBAL per-company status of a candidate (Naujas / Tinkamas / Abejotinas /
 * Netinkamas) — set it once and it reflects on every job's applications page
 * and the CV detail page.
 *
 * All methods unwrap the standard `{ success, data }` envelope and throw on
 * `success: false`.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export type EvaluationValue = 'pending' | 'accepted' | 'doubtful' | 'rejected';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CandidateEvaluation {
  id: number;
  employer_id: number;
  candidate_id: number;
  evaluation: EvaluationValue;
  created_at: string;
  updated_at: string;
}

export interface CandidateEvaluationSummary {
  candidate_id: number;
  evaluation: EvaluationValue;
  updated_at: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Record<string, string>;
  message?: string;
}

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await promise;
  if (!res.data?.success) {
    const err: any = new Error(res.data?.error || 'Request failed');
    err.fieldErrors = res.data?.errors;
    throw err;
  }
  return res.data.data as T;
}

export const candidateEvaluationsService = {
  /**
   * Bulk-fetch evaluations for a list of candidate ids. Returns a
   * `Record<candidateId, summary>` shape so callers can decorate cards in O(1).
   */
  bulk: async (candidateIds: number[]) => {
    if (candidateIds.length === 0) {
      return {} as Record<string, CandidateEvaluationSummary>;
    }
    const params = new URLSearchParams();
    params.set('candidate_ids', candidateIds.join(','));
    const res = await unwrap<{ evaluations: Record<string, CandidateEvaluationSummary> }>(
      axios.get(`${API_URL}/candidate-evaluations?${params.toString()}`, {
        headers: authHeaders(),
      }),
    );
    return res.evaluations;
  },

  getOne: (candidateId: number) =>
    unwrap<{ evaluation: CandidateEvaluation | null }>(
      axios.get(`${API_URL}/candidate-evaluations/${candidateId}`, {
        headers: authHeaders(),
      }),
    ),

  save: (candidateId: number, evaluation: EvaluationValue) =>
    unwrap<{ evaluation: CandidateEvaluation }>(
      axios.put(
        `${API_URL}/candidate-evaluations/${candidateId}`,
        { evaluation },
        { headers: authHeaders() },
      ),
    ),
};
